import { Button, Dialog, Divider, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SdxAlert } from '@sodexo/ui-components';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { Catalog } from '../../../Models/Catalog';
import { useCatalogs } from '../../../Queries/catalogs.queries';
import { useStore } from '../../../store/store';
import { Colors } from '../../../theme';
import { excludeParamsFromUrl, parseUrlQuery, stringifyToQueryUrl, UrlKeys } from '../../Utils/UrlUtil';
import { CatalogSelectorCollapsed } from './CatalogSelectorCollapsed';

const ErrorAlert = styled(SdxAlert)(({ theme }) => ({
  position: 'absolute',
  right: 16,
  top: 100,
}));

const CustomButton = styled(Button)(({ theme }) => ({
  border: `2px solid ${Colors.blue}`,
  borderRadius: 8,
  width: 120,
  '&.selected': {
    backgroundColor: Colors.lightBlue,
    borderColor: Colors.lightBlue,
    color: 'white',
  },
}));

export function GlobalCatalogSelector() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const [catalog, clearStorage] = useStore(useShallow(state => [state.catalog, state.clearStorage]));
  const { handleSubmit, setValue, watch } = useForm<Partial<Catalog>>({
    defaultValues: catalog ? new Catalog(catalog) : undefined,
  });
  const [enableFetchCatalogs, setEnableFetchCatalogs] = useState(false);
  const catalogs = useCatalogs({ enabled: enableFetchCatalogs });

  const formState = watch();

  const openDialog = () => {
    setEnableFetchCatalogs(true);
    setOpen(true);
  };
  const closeDialog = () => setOpen(false);

  // Update the url with catalog params on every route changes to always have it un the url in case someone share the url to someone else
  useEffect(() => {
    const urlQuery = parseUrlQuery(location);
    if (!urlQuery[UrlKeys.Catalog] || !urlQuery[UrlKeys.Organization] || !urlQuery[UrlKeys.Taxonomy]) {
      const urlParamsWithoutCatalogsParams = excludeParamsFromUrl(location, [
        UrlKeys.Catalog,
        UrlKeys.Organization,
        UrlKeys.Taxonomy,
      ]);
      navigate(`${location.pathname}?${urlParamsWithoutCatalogsParams}&${catalog?.buildUrlParams()}`, {
        replace: true,
      });
    }
  }, [location.search, catalog]);

  const onSubmit = (formData: Partial<Catalog>) => {
    const hasOrganizationChanged = catalog && catalog.organization !== formData.organization;
    const hasCatalogChanged = catalog && catalog.name !== formData.name;
    const hasTaxonomyChanged = catalog && catalog.taxonomy !== formData.taxonomy;
    if (hasOrganizationChanged || hasCatalogChanged || hasTaxonomyChanged) {
      const catalogParams = stringifyToQueryUrl({
        [UrlKeys.Catalog]: formData.name,
        [UrlKeys.Organization]: formData.organization,
        [UrlKeys.Taxonomy]: formData.taxonomy,
      });
      clearStorage();
      navigate(`/?${catalogParams}`, { replace: true });
      history.go(0);
    } else {
      // Nothing changed, just close the dialog
      closeDialog();
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CatalogSelectorCollapsed openDialog={openDialog} catalog={catalog} />
      {catalogs.error && <ErrorAlert severity='error' title={catalogs.error.message} />}
      <Dialog
        fullWidth
        maxWidth='sm'
        onClose={closeDialog}
        open={open}
        PaperProps={{
          sx: { p: 4 },
        }}
      >
        {catalogs.isLoading ? (
          <LoadingTemplate />
        ) : (
          <Stack spacing={5}>
            <Stack spacing={2} data-testid='catalog-selector-organizations'>
              <Typography variant='h3'>{t('components.globalCatalogSelector.step1Title')}</Typography>
              <Typography variant='body2'>{t('components.globalCatalogSelector.step1Subtitle')}</Typography>
              <Grid container spacing={1} justifyContent='center' width={1}>
                {catalogs.data?.map(c => (
                  <Grid key={c.organization} size={{ xs: 3 }}>
                    <CustomButton
                      className={clsx({ selected: c.organization === formState.organization })}
                      onClick={() => {
                        setValue('organization', c.organization);
                        setValue('name', undefined);
                        setValue('taxonomy', undefined);
                      }}
                    >
                      {c.organization}
                    </CustomButton>
                  </Grid>
                ))}
              </Grid>
            </Stack>

            <Stack spacing={2} data-testid='catalog-selector-catalogs'>
              <Typography variant='h3'>{t('components.globalCatalogSelector.step2Title')}</Typography>
              <Typography variant='body2'>{t('components.globalCatalogSelector.step2Subtitle')}</Typography>
              <Grid container spacing={1} justifyContent='center' width={1}>
                {catalogs.data
                  ?.find(c => c.organization === formState.organization)
                  ?.catalogs.map(c => (
                    <Grid key={c.name} size={{ xs: 3 }}>
                      <CustomButton
                        className={clsx({ selected: c.name === formState.name })}
                        onClick={() => {
                          setValue('name', c.name);
                          setValue('taxonomy', undefined);
                          setValue('language', c.language);
                        }}
                      >
                        {c.name}
                      </CustomButton>
                    </Grid>
                  ))}
              </Grid>
            </Stack>

            <Stack spacing={2} data-testid='catalog-selector-taxonomies'>
              <Typography variant='h3'>{t('components.globalCatalogSelector.step3Title')}</Typography>
              <Typography variant='body2'>{t('components.globalCatalogSelector.step3Subtitle')}</Typography>
              <Grid container spacing={1} justifyContent='center' width={1}>
                {(
                  catalogs.data
                    ?.find(c => c.organization === formState.organization)
                    ?.catalogs.find(c => c.name === formState.name)?.taxonomies ||
                  catalogs.data?.find(c => c.organization === formState.organization)?.catalogs[0].taxonomies
                )?.map(t => (
                  <Grid key={t} size={{ xs: 3 }}>
                    <CustomButton
                      className={clsx({ selected: t === formState.taxonomy })}
                      onClick={() => setValue('taxonomy', t)}
                    >
                      {t}
                    </CustomButton>
                  </Grid>
                ))}
              </Grid>
            </Stack>

            <Button
              type='submit'
              sx={{ alignSelf: 'center', width: 256, backgroundColor: Colors.lightBlue }}
              onClick={() => handleSubmit(onSubmit)()}
              disabled={!formState.organization || !formState.name || !formState.taxonomy}
              variant='contained'
            >
              {t('common.apply')}
            </Button>

            {catalogs.error && <p>{JSON.stringify(catalogs.error)}</p>}
          </Stack>
        )}
      </Dialog>
    </form>
  );
}

function LoadingTemplate() {
  return (
    <Stack spacing={5}>
      <Stack spacing={2}>
        <Skeleton variant='text' width={200} height={25} />
        <Grid container spacing={2} justifyContent='center' width={1}>
          <Grid size={{ xs: 4 }}>
            <Skeleton variant='rectangular' height={40} />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Skeleton variant='rectangular' height={40} />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Skeleton variant='rectangular' height={40} />
          </Grid>
        </Grid>
        <Divider />
      </Stack>

      <Stack spacing={2}>
        <Skeleton variant='text' width={200} height={25} />
        <Grid container spacing={2} justifyContent='center' width={1}>
          <Grid size={{ xs: 4 }}>
            <Skeleton variant='rectangular' height={40} />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Skeleton variant='rectangular' height={40} />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Skeleton variant='rectangular' height={40} />
          </Grid>
        </Grid>
        <Divider />
      </Stack>

      <Stack spacing={2}>
        <Skeleton variant='text' width={200} height={25} />
        <Grid container spacing={2} justifyContent='center' width={1}>
          <Grid size={{ xs: 4 }}>
            <Skeleton variant='rectangular' height={40} />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Skeleton variant='rectangular' height={40} />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Skeleton variant='rectangular' height={40} />
          </Grid>
        </Grid>
        <Divider />
      </Stack>

      <Skeleton variant='rectangular' width={100} height={40} sx={{ alignSelf: 'flex-end' }} />
    </Stack>
  );
}
